import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SalesforceEntityIconComponent } from 'entity-icon-lib';
import { RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IEntityMetadata, IRuntimeEnvironment } from 'interfaces-lib';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { UtilsService } from 'utils-service-lib';
import { IAutoCompleteItem } from './iautocompleteitem';

@Component({
  selector: 'app-kp-autocomplete',
  templateUrl: 'auto-complete.component.html',
  styleUrls: ['auto-complete.component.scss'],
  imports: [CommonModule, MatTooltipModule, MatIconModule, SalesforceEntityIconComponent],
})
export class AutoCompleteComponent<T> implements AfterViewInit {
  @ViewChild('searchInput') searchInput: ElementRef | undefined;

  @Input()
  set selected(value: string | undefined) {
    this.selectedValue = value ?? '';
  }

  @Input()
  filterFunction: ((value: string) => Observable<IAutoCompleteItem<T>[]>) | undefined;

  @Input()
  tabHandlerFunction: ((input: string, item: IAutoCompleteItem<T> | undefined) => string | undefined) | undefined;

  @Input()
  placeholder: string | undefined = '';

  @Input()
  focusInputOnLoad = false;

  @Input()
  disabled = false;

  @Input()
  valid = true;

  @Input()
  position: 'absolute' | 'initial' | 'fixed' | 'default' = 'default';

  @Input()
  embeddedDropDown = false;

  @Input()
  clearOnSelection = false;

  @Input()
  inputWidth = '100%';

  @Input()
  entitiesMeta: IEntityMetadata[] | undefined;

  @Input()
  showItemTooltip = false;

  @Input()
  showItemIconTooltip = false;

  @Input()
  scrollOnOptionsChange = true;

  @Input()
  maxHeight = '336px';

  @Input() minWidth: string | undefined;

  @Input()
  expandUp = false;

  @Input()
  dataLoading? = false;

  @Output()
  selectedChange = new EventEmitter<T>();

  @Output()
  inputCleared = new EventEmitter<void>();

  // TODO - JC - any type here should be changed back to IAutoCompleteItem.
  //  Currently we are supporting IPickerInfo too from the document client app.
  items$: Observable<any[]>;
  items: IAutoCompleteItem<T>[] = [];
  hide = true;
  highlightItemIndex = -1;
  selectedValue = '';
  searching = false;
  renderMode = 'web-app';
  selectedType: string | undefined;

  private valueChangesSubject = new Subject<string>();
  private valueChanges$ = this.valueChangesSubject.asObservable();
  private itemHeight = 0;
  private visibleItemCount = 0;
  private keyHandlers: { [index: string]: () => void } = {
    ArrowDown: this.arrowKeyDownHandler.bind(this),
    ArrowUp: this.arrowKeyUpHandler.bind(this),
    Enter: this.enterKeyHandler.bind(this),
    Escape: this.escapeKeyHandler.bind(this),
  };
  private suppressDefaultKeyBehaviours = ['ArrowUp', 'ArrowDown', 'Enter'];
  private ignoreDocumentClick = false;

  @ViewChild('dropDownContainer')
  dropdownContainer: ElementRef | undefined;

  @ViewChild('searchBox') searchBox: ElementRef | undefined;

  constructor(
    private utilsService: UtilsService,
    @Inject(RUNTIME_ENVIRONMENT) private runtimeEnvironment: IRuntimeEnvironment | undefined
  ) {
    this.renderMode = this.runtimeEnvironment?.renderMode ?? 'web-app';
    this.items$ = this.valueChanges$.pipe(
      debounceTime(400),
      tap((_) => (this.searching = true)),
      switchMap((value) => (this.filterFunction ? this.filterFunction(value) : of([]))),
      tap((_) => (this.searching = false)),
      tap((result) => {
        if (this.utilsService.dialogOpen()) {
          this.utilsService.escapableElementActive(true);
        }
        this.items = result;
        this.highlightItemIndex = result.length > 0 ? 0 : -1;
        this.calculateDropdownItemDimensions();
        if (this.scrollOnOptionsChange) {
          this.scrollResultsIntoView();
        }
      })
    );
  }

  @HostListener('click')
  componentClick() {
    this.ignoreDocumentClick = true;
  }

  @HostListener('document:click')
  documentClick() {
    if (!this.ignoreDocumentClick) {
      this.hide = true;
    }
    this.ignoreDocumentClick = false;
  }

  // Keyboard Shortcuts - Use Shift + Ctrl + F to Search
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchBox = this.searchBox?.nativeElement;

    if (!this.utilsService.dialogOpen() && !this.utilsService.escapableElementActive() && event.ctrlKey && event.shiftKey) {
      if (event.code === 'KeyF') {
        searchBox?.focus();
        searchBox?.select();
      }
    }
    if (this.isTabCompletionKey(event) && document.activeElement === this.searchInput?.nativeElement) {
      if (this.itemSelected()) {
        event.preventDefault();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.focusInputOnLoad) {
      this.searchInput?.nativeElement.focus();
    }
  }

  //region UI Event Handlers
  input(e: any): void {
    this.hide = false; // tap in the value change?
    this.valueChangesSubject.next(e.target.value);
  }

  keydown(e: any): void {
    if (e.repeat) {
      if (e.key === 'ArrowDown') {
        this.arrowKeyDownHandler();
      }
      if (e.key === 'ArrowUp') {
        this.arrowKeyUpHandler();
      }
    }
    if (this.items.length === 0 && this.searchBox?.nativeElement.value.length === 0) {
      this.utilsService.escapableElementActive(false);
    }

    // e.g. prevent the caret jumping to the start / end of the text, or Enter submitting an enclosing form
    if (this.suppressDefaultKeyBehaviours.findIndex((key) => e.key === key) > -1) {
      e.preventDefault();
    }
  }

  keyup(e: any): void {
    const handler = this.keyHandlers[e.key];
    if (handler) {
      handler();
      e.preventDefault();
    }
  }

  blur(_e: any): void {
    // Blur fires when hitting Esc. Intention: Hitting Esc while focussed on input will clear the value.
    // Clicking/tabbing out then hitting Esc will close dialog if open
    setTimeout((_: any) => {
      this.utilsService.escapableElementActive(false);
    }, 200);
  }

  focus(e: any): void {
    if (e.target.value.length > 0) {
      this.utilsService.escapableElementActive(true);
    }
  }

  click(e: any): void {
    this.valueChangesSubject.next(e.target.value);
    this.hide = false;
  }

  select(e: any): void {
    if (!this.tabHandlerFunction && this.searchInput) {
      this.clearOnSelection ? (this.selectedValue = '') : (this.selectedValue = e.label);
      this.searchInput.nativeElement.value = this.selectedValue;
      this.selectedType = this.entitiesMeta
        ? this.entitiesMeta.find((entity) => entity.name.toLowerCase() === e.entity.type.toLowerCase())?.label
        : '';
      this.hide = true;
      this.items = [];
      this.highlightItemIndex = -1;
      this.selectedChange.emit(e.data ?? e);
    } else {
      this.updateSelectedItem(e);
    }
  }

  clearSelection(): void {
    this.clearInput(this.searchInput?.nativeElement, false);
  }

  clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    if (!inputElement) {
      return;
    }

    const input = inputElement as HTMLInputElement;
    input.value = '';
    this.selectedValue = '';
    if (focusInput) {
      inputElement.focus();
    }
    if (this.items.length > 0) {
      this.utilsService.escapableElementActive(false);
    }
    this.items = [];
    this.valueChangesSubject.next(input.value);
    this.inputCleared.emit();
  }

  // TODO - JC - replace any type here
  //  Currently we are supporting IPickerInfo too from the document client app.
  itemTooltip(item: any): string | undefined {
    if (!this.showItemTooltip) {
      return undefined;
    }

    if (item.tooltipOverride) {
      return item.tooltipOverride;
    }

    let title = item.label;

    if (item.showId) {
      title += ` (${item.entity.id})`;
      if (item.details) {
        title += ' -';
      }
    }

    if (item.details) {
      title += ` ${item.details}`;
    }

    return title;
  }

  // TODO - JC - replace any type here
  //  Currently we are supporting IPickerInfo too from the document client app.
  itemIconTooltip(item: any): string | undefined {
    if (!this.showItemIconTooltip) {
      return undefined;
    }
    /*
      TODO: Support Invenias
      * item.entity.type is not defined for Invenias. Use item.data.ItemType.
    */
    return this.entitiesMeta?.find((entity) => entity.name.toLowerCase() === item.entity?.type?.toLowerCase())?.label;
  }

  onItemHover(index: number): void {
    this.highlightItemIndex = index;
  }

  //endregion

  //region Private Functions
  private arrowKeyDownHandler(): void {
    const index = this.highlightItemIndex + 1;
    this.highlightItemIndex = index < this.items.length ? index : this.items.length - 1;
    if (index !== this.highlightItemIndex) {
      return;
    }

    const highlightY = this.highlightItemIndex * this.itemHeight;
    const scrollTop = this.dropdownContainer?.nativeElement.scrollTop;
    const clientHeight = this.visibleItemCount * this.itemHeight;
    if (highlightY < scrollTop || highlightY > scrollTop + clientHeight) {
      this.dropdownContainer?.nativeElement.scrollTo(0, this.highlightItemIndex * this.itemHeight);
    } else if (highlightY + this.itemHeight > scrollTop + clientHeight) {
      // make sure the selected item is entirely on screen when it is at the end of the visible range
      const diff = highlightY + this.itemHeight - (scrollTop + clientHeight);
      this.dropdownContainer?.nativeElement.scrollBy(0, diff);
    }
  }

  private arrowKeyUpHandler(): void {
    const index = this.highlightItemIndex - 1;
    this.highlightItemIndex = index > -1 ? index : 0;
    if (index !== this.highlightItemIndex) {
      return;
    }

    const highlightY = this.highlightItemIndex * this.itemHeight;
    const scrollTop = this.dropdownContainer?.nativeElement.scrollTop;
    const clientHeight = this.visibleItemCount * this.itemHeight;
    if (highlightY < scrollTop || highlightY > scrollTop + clientHeight) {
      this.dropdownContainer?.nativeElement.scrollTo(0, this.highlightItemIndex * this.itemHeight);
    }
  }

  private enterKeyHandler(): void {
    if (this.highlightItemIndex > -1) {
      this.select(this.items[this.highlightItemIndex]);
    }
    this.hide = true;
  }

  private escapeKeyHandler(): void {
    this.clearInput(this.searchInput?.nativeElement, false);
    this.hide = true;
  }

  private calculateDropdownItemDimensions(): void {
    setTimeout(() => {
      if (this.items.length === 0) {
        this.itemHeight = 0;
        this.visibleItemCount = 0;
        return;
      }

      this.itemHeight = this.dropdownContainer?.nativeElement.childNodes[0].clientHeight;
      // tslint:disable-next-line:no-bitwise
      this.visibleItemCount = (this.dropdownContainer?.nativeElement.clientHeight / this.itemHeight) | 0;
    });
  }

  private scrollResultsIntoView(): void {
    setTimeout((_: any) => {
      this.dropdownContainer?.nativeElement.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }, 0);
  }

  private isTabCompletionKey(event: KeyboardEvent): boolean {
    return event.code === 'Tab' && !event.shiftKey && !!this.tabHandlerFunction;
  }

  private itemSelected(): boolean {
    const item = this.highlightItemIndex > -1 ? this.items[this.highlightItemIndex] : undefined;
    if (!item || !this.updateSelectedItem(item)) {
      return false;
    }

    this.searchBox?.nativeElement.focus();
    return true;
  }

  private updateSelectedItem(item: IAutoCompleteItem<T>): boolean {
    if (!this.tabHandlerFunction || !this.searchInput) {
      return false;
    }

    const inputValue = this.searchInput.nativeElement.value;
    const newValue = this.tabHandlerFunction(inputValue, item);

    if (!newValue || newValue === inputValue) {
      return false;
    }

    this.searchInput.nativeElement.value = newValue;
    this.valueChangesSubject.next(newValue);
    this.selectedChange.emit(this.searchInput.nativeElement.value);
    return true;
  }

  //endregion
}
